import { Button, Form, Input, Select, Switch, Table, InputNumber, message } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../utils/useAuth";
import axios from "axios";
import { BASE_API_URL } from "../../../utils/consts";
import { useDispatch, useSelector } from "react-redux";
import { setUserData } from "../../../utils/redux/slices/userDataSlice";
import { useEffect, useState } from "react";
import {
  ExchangeRate,
  getExchangeRates,
  upsertExchangeRate,
  fetchLiveExchangeRate,
} from "../../../utils/api/exchangeRateAPI";

const { Option } = Select;

// Client.currency is a free-text label — map to the ISO code used across
// Credit.currency / ExchangeRate so the two settings stay in sync.
const CURRENCY_NAME_TO_CODE: Record<string, string> = {
  Dinar: "RSD",
  Euro: "EUR",
  Dollar: "USD",
};
const ALL_CURRENCY_CODES = ["RSD", "EUR", "USD", "BAM", "CHF", "GBP"];

const GeneralSettings = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state.userData.user);
  const authUser: any = useAuth();

  useEffect(() => {
    if (userData?.client?.name) {
      form.setFieldsValue({
        companyName: userData?.client?.name,
        currency: userData?.client?.currency,
        notificationEmail: userData?.client?.settings?.notificationEmail,
        showEmployeeSalariesInDashboard:
          !!userData?.client?.settings?.showEmployeeSalariesInDashboard,
      });
    }
  }, [userData, form]);

  // ── Exchange rates (used to convert credits held in a foreign currency,
  //    e.g. EUR loans, into the base currency shown on the Dashboard) ────────
  const clientId = authUser?.clientId;
  const baseCurrency =
    CURRENCY_NAME_TO_CODE[userData?.client?.currency] || "RSD";
  const otherCurrencies = ALL_CURRENCY_CODES.filter((c) => c !== baseCurrency);

  const [rates, setRates] = useState<ExchangeRate[]>([]);
  const [rateDrafts, setRateDrafts] = useState<Record<string, number | null>>({});
  const [rateLoading, setRateLoading] = useState<Record<string, boolean>>({});

  const loadRates = () => {
    if (!clientId) return;
    getExchangeRates(clientId).then(setRates).catch(() => {});
  };

  useEffect(() => {
    loadRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const rateFor = (code: string) =>
    rates.find((r) => r.fromCurrency === code && r.toCurrency === baseCurrency);

  const setLoading = (code: string, val: boolean) =>
    setRateLoading((prev) => ({ ...prev, [code]: val }));

  const handleSaveRate = async (code: string) => {
    const draft = rateDrafts[code] ?? rateFor(code)?.rate;
    if (!draft || draft <= 0) return;
    setLoading(code, true);
    try {
      await upsertExchangeRate(clientId, code, baseCurrency, draft);
      message.success(t("Exchange rate saved"));
      loadRates();
    } catch {
      message.error(t("Failed to save exchange rate"));
    } finally {
      setLoading(code, false);
    }
  };

  const handleFetchLive = async (code: string) => {
    setLoading(code, true);
    try {
      const updated = await fetchLiveExchangeRate(clientId, code, baseCurrency);
      message.success(t("Exchange rate updated"));
      setRateDrafts((prev) => ({ ...prev, [code]: updated.rate }));
      loadRates();
    } catch {
      message.error(t("Could not fetch live exchange rate"));
    } finally {
      setLoading(code, false);
    }
  };

  const getCurrencyIcon = (currency: string) => {
    switch (currency) {
      case "Dinar":
        return "RSD";
      case "Euro":
        return "€";
      case "Dollar":
        return "$";
      default:
        return "€";
    }
  };

  const onFinish = (value: any) => {
    const currencyIcon = getCurrencyIcon(value.currency);

    axios
      .put(`${BASE_API_URL}/client/${authUser.clientId}`, {
        name: value.companyName,
        currency: value.currency,
        currencyIcon: currencyIcon,
        settings: {
          notificationEmail: value.notificationEmail?.trim() || null,
          showEmployeeSalariesInDashboard: !!value.showEmployeeSalariesInDashboard,
        },
      })
      .then((res) => {
        let updatedUserData: any = JSON.parse(JSON.stringify(userData));

        // Update client object
        if (updatedUserData.client) {
          updatedUserData.client.name = res.data.name;
          updatedUserData.client.currency = res.data.currency;
          updatedUserData.client.currencyIcon = res.data.currencyIcon;
          updatedUserData.client.settings = res.data.settings;
        }

        // Update clientId object if it exists and is an object (not just a string ID)
        if (
          updatedUserData.clientId &&
          typeof updatedUserData.clientId === "object"
        ) {
          updatedUserData.clientId.currency = res.data.currency;
          updatedUserData.clientId.currencyIcon = res.data.currencyIcon;
        }

        dispatch(setUserData(updatedUserData));
        localStorage.setItem("user", JSON.stringify(updatedUserData));
      });
  };

  const onFinishFailed = (value: any) => {};
  return (
    <div className="settingsTab">
      <div className="settingsForm">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
        >
          <Form.Item
            label={t("Company Name")}
            name="companyName"
            tooltip={t("This is a required field")}
            rules={[{ required: true, message: t("Please enter name") }]}
          >
            <Input placeholder={t("Enter Company Name")} />
          </Form.Item>
          <Form.Item
            name="currency"
            label={t("Currency")}
            rules={[
              {
                required: true,
                message: t("Please select currency type"),
              },
            ]}
          >
            <Select placeholder={t("Please select currency type")}>
              <Option value={"Dinar"} key={"dinar"}>
                {t("Dinar")}
              </Option>
              <Option value={"Euro"} key={"euro"}>
                {t("Euro")}
              </Option>
              <Option value={"Dollar"} key={"dollar"}>
                {t("Dollar")}
              </Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="notificationEmail"
            label={t("Notification Email")}
            tooltip={t(
              "Order and reservation notifications (e.g. breakfast orders) will be sent to this email"
            )}
            rules={[{ type: "email", message: t("Enter a valid email") }]}
          >
            <Input placeholder={t("e.g. info@yourcompany.com")} />
          </Form.Item>
          <Form.Item
            name="showEmployeeSalariesInDashboard"
            label={t("Show employee salaries in Dashboard costs")}
            tooltip={t(
              "When enabled, the Dashboard's cost breakdown will include a section with net employee salaries (excluding loans) and factor it into the profit calculation"
            )}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          {/* <Form.Item
        label="Field B"
        tooltip={{
          title: "Tooltip with customize icon",
          icon: <InfoCircleOutlined />,
        }}
      >
        <Input placeholder="input placeholder" />
      </Form.Item> */}
          <Form.Item>
            <Button type="primary" htmlType="submit">
              {t("Save")}
            </Button>
          </Form.Item>
        </Form>

        <div style={{ marginTop: 32 }}>
          <h3 style={{ marginBottom: 4 }}>{t("Exchange Rates")}</h3>
          <p style={{ color: "#8c8c8c", marginBottom: 16, maxWidth: 560 }}>
            {t(
              "Used to convert credits/loans held in a foreign currency into your base currency ({{base}}) on the Dashboard.",
              { base: baseCurrency }
            )}
          </p>
          <Table
            rowKey="code"
            pagination={false}
            dataSource={otherCurrencies.map((code) => ({ code }))}
            columns={[
              {
                title: t("Currency"),
                dataIndex: "code",
                render: (code: string) => `${code} → ${baseCurrency}`,
              },
              {
                title: t("Rate"),
                dataIndex: "rate",
                render: (_: any, row: { code: string }) => (
                  <InputNumber
                    min={0}
                    step={0.01}
                    style={{ width: 140 }}
                    value={rateDrafts[row.code] ?? rateFor(row.code)?.rate}
                    onChange={(val) =>
                      setRateDrafts((prev) => ({ ...prev, [row.code]: val }))
                    }
                    placeholder={t("Not set")}
                  />
                ),
              },
              {
                title: t("Source"),
                dataIndex: "source",
                render: (_: any, row: { code: string }) => {
                  const r = rateFor(row.code);
                  if (!r) return <span style={{ color: "#ff4d4f" }}>{t("Not set")}</span>;
                  return r.source === "auto" ? t("Auto-fetched") : t("Manual");
                },
              },
              {
                title: "",
                dataIndex: "actions",
                render: (_: any, row: { code: string }) => (
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button
                      size="small"
                      loading={!!rateLoading[row.code]}
                      onClick={() => handleSaveRate(row.code)}
                    >
                      {t("Save")}
                    </Button>
                    <Button
                      size="small"
                      icon={<SyncOutlined />}
                      loading={!!rateLoading[row.code]}
                      onClick={() => handleFetchLive(row.code)}
                    >
                      {t("Fetch current rate")}
                    </Button>
                  </div>
                ),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default GeneralSettings;
